import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Slider from 'react-slick';
import { FaExternalLinkAlt, FaBuilding, FaChevronLeft, FaChevronRight, FaSpinner } from 'react-icons/fa';
import axiosClient from '../../services/axiosClient';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../assets/css/modern-fluid-carousel.css';

/**
 * Modern Fluid Carousel Component
 * Ultra-modern carousel with fluid animations and elegant floating content
 */

// Custom Arrow Components
const PrevArrow = ({ onClick, currentSlide, slideCount }) => (
  <button 
    className="carousel-arrow carousel-arrow-prev" 
    onClick={onClick}
    aria-label="Previous slide"
    disabled={currentSlide === 0}
  >
    <FaChevronLeft />
  </button>
);

const NextArrow = ({ onClick, currentSlide, slideCount }) => (
  <button 
    className="carousel-arrow carousel-arrow-next" 
    onClick={onClick}
    aria-label="Next slide"
    disabled={currentSlide === slideCount - 1}
  >
    <FaChevronRight />
  </button>
);

const ModernFluidCarousel = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoized slider settings
  const sliderSettings = useMemo(() => ({
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
    pauseOnHover: true,
    pauseOnFocus: true,
    fade: true,
    cssEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    arrows: true,
    appendDots: dots => (
      <div>
        <ul className="custom-dots"> {dots} </ul>
      </div>
    ),
    customPaging: (i) => (
      <button 
        className="dot-button" 
        aria-label={`Go to slide ${i + 1}`}
      />
    )
  }), []);

  // Optimized fetch function with useCallback
  const fetchAds = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 ModernFluidCarousel: Fetching ads...');
      
      const response = await axiosClient.get('/ads');
      
      console.log('✅ ModernFluidCarousel: Response:', response.data);
      
      const adsData = Array.isArray(response.data) ? response.data : [];
      setAds(adsData);
      console.log('📊 ModernFluidCarousel: Loaded', adsData.length, 'ads');
    } catch (err) {
      console.error('❌ ModernFluidCarousel: Error:', err);
      setError('Impossible de charger les publicités');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  // Loading state component
  if (loading) {
    return (
      <div className="modern-fluid-carousel">
        <div className="carousel-loading">
          <div className="loading-spinner">
            <FaSpinner className="spinner-icon" />
          </div>
          <p>Chargement des publicités...</p>
        </div>
      </div>
    );
  }

  // Error state component
  if (error) {
    return (
      <div className="modern-fluid-carousel">
        <div className="carousel-error">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          <button onClick={fetchAds} className="retry-button">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Empty state component
  if (!ads || ads.length === 0) {
    return (
      <div className="modern-fluid-carousel">
        <div className="carousel-empty">
          <div className="empty-icon">📢</div>
          <p>Aucune publicité disponible pour le moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-fluid-carousel">
      <div className="carousel-container">
        <Slider {...sliderSettings}>
          {ads.map((ad, index) => (
            <div key={ad.id || index} className="carousel-slide">
              <div className="ad-card-fluid">
                {/* Image Section */}
                <div className="ad-image-section">
                  <img 
                    src={ad.imageUrl || '/placeholder-ad.jpg'} 
                    alt={ad.title || 'Publicité'}
                    className="ad-image"
                    loading="lazy"
                  />
                  <div className="ad-image-overlay"></div>
                </div>

                {/* Ultra Modern Floating Content */}
                <div className="ad-content-section-minimal">
                  {/* Company Badge */}
                  {ad.company && (
                    <div className="ad-company-badge-minimal">
                      <FaBuilding className="company-icon" />
                      <span>{ad.company}</span>
                    </div>
                  )}

                  {/* CTA Button */}
                  {ad.ctaUrl && (
                    <a
                      href={ad.ctaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ad-cta-button-minimal"
                      aria-label={`En savoir plus sur ${ad.company}`}
                    >
                      <span>{ad.ctaLabel || 'En savoir plus'}</span>
                      <FaExternalLinkAlt className="cta-icon" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>
    </div>
  );
};

export default ModernFluidCarousel;
